import { Box, Button, Container, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import { ObjectLayoutContextProvider } from "./ObjectLayoutContext";
import ObjectLayoutEditor from "./ObjectLayoutEditor";
import ObjectLayoutPageTitle from "./ObjectLayoutPageTitle";
import { getCreatePageLayoutRequest, getInitialState, initLayoutAction } from "./objectLayoutState";

const saveLayout = withErrorHandling(adminApi.saveObjectConfigurationLayout);

const ObjectLayoutPage = () => {
  const { objectDefinition, hasEditPermissions } = useObjectDefinitionContext();
  usePageTitle(`${objectDefinition.objectName} | Data Model Layout`);
  const { configurationId } = useParams();
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [layoutState, setLayoutState] = useState(() => getInitialState(objectDefinition, configurationId ?? ""));
  const [isSaving, setIsSaving] = useState(false);

  const pathToObjectLayouts = `/${clientCode}/${pageRoutes.settings}/${pageRoutes.dataModel}/${pageRoutes.orgObjects}/${objectDefinition.objectType}?tab=layouts`;

  const getLayout = useCallback(
    async () => adminApi.getObjectConfigurationLayout(objectDefinition.objectType, configurationId ?? ""),
    [configurationId, objectDefinition.objectType]
  );

  const [pageLayout, layoutFetchError, { isFetching }] = useFetch(getLayout, {
    onSetData: (data) => setLayoutState(initLayoutAction(data)),
  });

  if (!configurationId || !hasEditPermissions) {
    return <Navigate to={pathToObjectLayouts} />;
  }

  if (layoutFetchError) {
    logError(layoutFetchError, "[ObjectLayoutPage] getLayout");
    return <DataLoadingFailed title="Failed to load page layout" />;
  }

  if (pageLayout === undefined || isFetching) {
    return <InlineLoader />;
  }

  const handleCancelClick = () => {
    setLayoutState(initLayoutAction(pageLayout));
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    const [savedLayout, error] = await saveLayout(objectDefinition.objectType, getCreatePageLayoutRequest(layoutState));
    setIsSaving(false);

    if (error) {
      logError(error, "[ObjectLayoutPage] saveLayout");
      sendNotificationError(error.message);
      return;
    }

    setLayoutState(initLayoutAction(savedLayout));
    sendNotification("Layout saved successfully");
  };

  return (
    <ObjectLayoutContextProvider layoutState={layoutState} setLayoutState={setLayoutState}>
      <GeneralPageHeader TitleComponent={<ObjectLayoutPageTitle />} showDefaultBackButtonTo={pathToObjectLayouts}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            color="secondary"
            disabled={isSaving || !layoutState.isDirty || Boolean(layoutState.isAddingFieldToGroup)}
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <Button
            loading={isSaving}
            variant="contained"
            disabled={!layoutState.isDirty || Boolean(layoutState.isAddingFieldToGroup)}
            onClick={handleSaveClick}
          >
            Save Changes
          </Button>
        </Stack>
      </GeneralPageHeader>
      <Box sx={{ width: "100%", height: "100%", py: 2.5, bgcolor: "background.grey" }}>
        <Container maxWidth="md" disableGutters sx={{ height: "100%" }}>
          <ScrollableFlexContainer sx={{ height: "100%" }}>
            <ObjectLayoutEditor />
          </ScrollableFlexContainer>
        </Container>
      </Box>
    </ObjectLayoutContextProvider>
  );
};

export default ObjectLayoutPage;
